import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { formatError } from "../lib/api";
import { LoginForm } from "../components/auth/LoginForm";
import { RegisterForm } from "../components/auth/RegisterForm";

const Auth = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register"); // register | login
  const [busy, setBusy] = useState(false);

  const handleRegister = async ({ email, password, name }) => {
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success(`Welcome, ${name.split(" ")[0]}!`);
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleLogin = async ({ email, password }) => {
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleLabel =
    mode === "register" ? "Already have an account? Sign in" : "New here? Create an account";

  return (
    <div className="min-h-screen flex flex-col px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-2">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#277A82]">
            QFresh
          </span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold brand-gradient-text leading-tight tracking-tight">
          {mode === "register" ? "Create an account" : "Welcome back"}
        </h1>
        <p className="text-[#4F5D75] mt-3 max-w-sm">
          {mode === "register"
            ? "Track your fridge. Cut food waste. Share with your household."
            : "Sign in to see what's in your fridge."}
        </p>
      </motion.div>

      <div className="mt-8">
        {mode === "register" ? (
          <RegisterForm busy={busy} onSubmit={handleRegister} />
        ) : (
          <LoginForm busy={busy} onSubmit={handleLogin} />
        )}

        <button
          type="button"
          data-testid="auth-toggle-btn"
          onClick={() => setMode(mode === "register" ? "login" : "register")}
          className="w-full text-center text-sm text-[#4F5D75] hover:text-[#2D3142] pt-4"
        >
          {toggleLabel}
        </button>
      </div>
    </div>
  );
};

export default Auth;
